import { FC, useEffect, useMemo, useState } from "react";
import {
  MRT_ColumnDef,
  MRT_PaginationState,
  MantineReactTable,
  useMantineReactTable,
  MRT_ColumnFiltersState,
  MRT_SortingState,
} from "mantine-react-table";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store";
import { Box, Button } from "@mantine/core";
import { IconDownload } from "@tabler/icons-react";
import { downloadFile } from "@funded-here-interface/common/src/Utils/file";
import { FileType } from "@funded-here-interface/common/src/constant/enum";
import { toast } from "react-toastify";
import useDownloadTransactionsCSV from "../../../../hooks/useDownloadTransactionsCSV";
import { TransactionsData } from "../../../../services/getTransactionsData";
import useGetTransactionsData from "../../../../hooks/useGetTransactionsData";

const Transactions: FC = () => {
  const token = useSelector((state: RootState) => state.auth.token);

  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    []
  );
  const [sorting, setSorting] = useState<MRT_SortingState>([]);

  const useDownloadTransactionsCSVMutation = useDownloadTransactionsCSV();

  const apiColumnFilters: any = JSON.stringify(
    columnFilters
      .filter((filter) => {
        if (Array.isArray(filter.value)) {
          return filter.value.every(
            (v) => v !== null && v !== undefined && v !== ""
          );
        }
        return (
          filter.value !== null &&
          filter.value !== undefined &&
          filter.value !== ""
        );
      })
      .map((filter) => {
        let column = filter.id;
        let value = filter.value;

        if (column === "Upc ID") {
          column = "upcId";
        }
        return {
          [column]: value,
        };
      })
  );

  const apiSorting: any = JSON.stringify(
    sorting.reduce((acc, sort) => {
      if (sort.id && sort.desc !== undefined) {
        acc[sort.id] = sort.desc;
      }
      return acc;
    }, {} as Record<string, any>)
  );

  const { data, error, isLoading } = useGetTransactionsData(
    token,
    pagination,
    apiColumnFilters,
    apiSorting
  );

  if (error) {
    toast.error((error as Error).message);
  }

  const totalRowCount = data?.meta?.totalRowCount ?? 0;

  const handleDownloadTransactions = () => {
    useDownloadTransactionsCSVMutation.mutate(
      { token, apiColumnFilters, apiSorting },
      {
        onSuccess: (data: Blob) => {
          downloadFile(data, "Transaction", FileType.CSV);
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  };
  const columns = useMemo<MRT_ColumnDef<TransactionsData>[]>(
    () => [
      {
        accessorKey: "skuId",
        header: "Sku ID",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
      },
      {
        accessorKey: "merchantEmail",
        header: "Merchant Email",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
      },
      {
        accessorKey: "amount",
        header: "Amount",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
      },
      {
        accessorKey: "currency",
        header: "Currency",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
      },
      {
        accessorKey: "quantitySold",
        header: "Quantity Sold",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
        filterVariant: "range",
      },
      {
        accessorKey: "transactionId",
        header: "Transaction ID",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
      },
      {
        accessorKey: "transactionDate",
        header: "Transaction Date",
        filterVariant: "date-range",
      },
      {
        accessorKey: "createdAt",
        header: "Created Date",
        filterVariant: "date-range",
      },
    ],
    []
  );

  const table = useMantineReactTable({
    enableColumnActions: true,
    enableColumnFilters: true,
    enableDensityToggle: false,
    enableMultiSort: true,
    paginationDisplayMode: "pages",
    state: { pagination, isLoading, sorting }, //pass the pagination and isLoading state to the table
    columns,
    data: data?.data ?? [],
    manualPagination: true,
    onPaginationChange: setPagination, //hoist pagination state to your state when it changes internally
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    rowCount: totalRowCount, //pass the total row count to the table
    initialState: { density: "xs" },
    mantineTableProps: {
      withColumnBorders: true,
      withBorder: true,
    },
    mantineTableHeadCellProps: {
      style: {
        padding: "0.5rem 1.0rem",
        color: "#23262a",
      },
    },
    mantineTableBodyCellProps: {
      style: {
        padding: "0.5rem",
      },
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          display: "flex",
          gap: "16px",
          padding: "8px",
          flexWrap: "wrap",
        }}
      >
        <Button
          color="lightblue"
          //export all data that is currently in the table (ignore pagination, sorting, filtering, etc.)
          onClick={handleDownloadTransactions}
          leftIcon={<IconDownload />}
          variant="filled"
        >
          Export Transactions
        </Button>
      </Box>
    ),
  });

  return (
    <div>
      <div>
        <MantineReactTable table={table} />
      </div>
    </div>
  );
};

export default Transactions;
